import React, { useState, useEffect, useCallback } from "react";
import "./ImagePreviewDialog.css";
import { useS3SignedUrl } from "../hooks/useS3SignedUrl";

/**
 * Dialog component for previewing uploaded images from S3
 */
const ImagePreviewDialog = ({ isOpen, onClose, questionId, studentId }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API call to fetch image metadata
  const fetchImages = useCallback(async () => {
    if (!isOpen) return;

    // Validate required parameters
    if (!questionId || !studentId) {
      console.error("Missing required parameters:", { questionId, studentId });
      setError("Missing required parameters to fetch images");
      return;
    }

    console.log("Fetching image metadata...", { questionId, studentId });
    setLoading(true);
    setError(null);

    try {
      // Use the dynamic API URL with proper parameters
      const apiUrl = `https://procbackend.elcarreira.com/students/getUploadedFiles?questionId=${questionId}&studentId=${studentId}`;
      console.log("Fetching from API:", apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch uploaded images: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("API response:", data);

      if (!Array.isArray(data) || data.length === 0) {
        setImages([]);
        console.log("No images found in the response");
        return;
      }

      // Process image data
      const formattedImages = data
        .map((item, index) => {
          if (!item.bucketName || !item.fileURL) {
            console.error("Missing required S3 data:", item);
            return null;
          }

          return {
            id: index,
            originalUrl: item.fileURL,
            bucketName: item.bucketName,
            filename: item.fileURL.split("/").pop() || `file-${index + 1}`,
          };
        })
        .filter(Boolean);

      console.log(`Processed ${formattedImages.length} images`);
      setImages(formattedImages);

      // Select first image by default
      if (formattedImages.length > 0) {
        setSelectedImage(formattedImages[0]);
      }
    } catch (err) {
      console.error("Error fetching images:", err);
      setError("Failed to load images. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [isOpen, questionId, studentId]);

  // Fetch images when dialog opens or parameters change
  useEffect(() => {
    if (isOpen && questionId && studentId) {
      fetchImages();
    }
  }, [isOpen, questionId, studentId, fetchImages]);

  // Don't render anything if dialog is closed
  if (!isOpen) return null;

  return (
    <div className="image-preview-dialog-overlay">
      <div className="image-preview-dialog">
        <div className="dialog-header">
          <h3>Preview Uploaded Images</h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="dialog-content">
          {loading && (
            <div className="loading-indicator">
              <p>Loading images...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="sidebar-thumbnails">
                {images.length > 0 ? (
                  images.map((image) => (
                    <ImageThumbnail
                      key={image.id}
                      image={image}
                      isSelected={selectedImage?.id === image.id}
                      onSelect={() => setSelectedImage(image)}
                    />
                  ))
                ) : (
                  <div className="no-images">
                    <p>No uploaded images found</p>
                  </div>
                )}
              </div>

              <div className="main-preview">
                {selectedImage ? (
                  <ImageDetail image={selectedImage} />
                ) : (
                  <div className="empty-state">
                    <p>Select an image from the left panel to preview</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Thumbnail component for individual image preview
 */
const ImageThumbnail = React.memo(({ image, isSelected, onSelect }) => {
  const { signedUrl, isLoading, error } = useS3SignedUrl(
    image.bucketName,
    image.originalUrl
  );

  return (
    <div
      className={`thumbnail-item ${isSelected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <div className="thumbnail-number">{image.id + 1}</div>

      {isLoading ? (
        <div className="image-placeholder">
          <p>Loading...</p>
        </div>
      ) : error ? (
        <div className="image-error">
          <p>Error</p>
        </div>
      ) : (
        <div className="thumbnail-content">
          <img
            src={signedUrl}
            alt={`Thumbnail ${image.filename}`}
            onError={(e) => {
              console.error(`Failed to load thumbnail: ${image.filename}`);
              e.target.alt = "Image error";
              e.target.src = "/placeholder-image.png";
            }}
          />
        </div>
      )}
    </div>
  );
});

/**
 * Detail component for the selected image
 */
const ImageDetail = React.memo(({ image }) => {
  const { signedUrl, isLoading, error } = useS3SignedUrl(
    image.bucketName,
    image.originalUrl
  );

  if (isLoading) {
    return <div className="loading-preview">Loading image...</div>;
  }

  if (error) {
    return (
      <div className="error-preview">
        <p>Failed to load image</p>
        <div className="error-details">{error.message}</div>
      </div>
    );
  }

  return (
    <div className="selected-image-container">
      <div className="image-content">
        <img
          src={signedUrl}
          alt={`${image.filename}`}
          onError={(e) => {
            console.error(`Failed to load image: ${image.filename}`);
            e.target.alt = "Image loading error";
            e.target.src = "/placeholder-image.png";
          }}
        />
      </div>
    </div>
  );
});

export default ImagePreviewDialog;
